import React from 'react';
import { Box, Container, Heading, Text, VStack, SimpleGrid, Image, List, ListItem, ListIcon, Button, HStack, Icon, Divider } from '@chakra-ui/react';
import { FaCheckCircle, FaUsers, FaLightbulb, FaHandshake, FaClock, FaServer, FaBolt, FaLock, FaChartLine, FaCoins, FaChartBar, FaRocket, FaGift, FaCode, FaNetworkWired } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../components/Footer';

const About = () => {
  return (
    <Box bg="gray.900" minH="100vh" pt="80px" color="white">
      <Container maxW="container.xl" py={20}>
        <VStack spacing={20}>
          {/* Hero Section */}
          <VStack spacing={4} textAlign="center">
            <Heading 
              size="2xl" 
              bgGradient="linear(to-r, cyan.400, blue.500)" 
              bgClip="text"
              fontWeight="extrabold"
            >
              About HODL Protocol
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="800px">
              Simple and automatic SOL drops every 24 hours
            </Text>
          </VStack>

          {/* Mission Section */}
          <VStack spacing={8} textAlign="center">
            <Heading 
              size="2xl" 
              bgGradient="linear(to-r, cyan.400, blue.500)" 
              bgClip="text"
              fontWeight="extrabold"
            >
              Our Mission
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="800px">
              To create a simple, secure, and efficient rewards system that benefits all HODL token holders.
              We believe in making cryptocurrency rewards accessible to everyone.
            </Text>
          </VStack>

          {/* Moonit Integration Section */}
          <Box w="full" bg="blue.900" p={8} rounded="lg" boxShadow="xl">
            <VStack spacing={6}>
              <HStack>
                <Icon as={FaChartBar} w={8} h={8} color="cyan.400" />
                <Heading size="lg" color="cyan.400">Moonit Integration</Heading>
              </HStack>
              <Text fontSize="lg" textAlign="center" maxW="800px">
                The HODL token will be launched on Moonit using a bonding curve mechanism, ensuring fair token distribution and price discovery.
              </Text>
              <Text fontSize="md" textAlign="center" maxW="800px" color="gray.300">
                <strong>Important:</strong> Rewards will begin 24 hours after the token meets the Moonit bonding curve criteria.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaRocket} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">Fair Launch</Heading>
                  <Text textAlign="center" color="gray.300">
                    Bonding curve ensures fair token distribution
                  </Text>
                </VStack>
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaClock} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">24hr Delay</Heading>
                  <Text textAlign="center" color="gray.300">
                    Rewards begin 24 hours after bonding curve criteria is met
                  </Text>
                </VStack>
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaGift} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">Automatic Rewards</Heading>
                  <Text textAlign="center" color="gray.300">
                    SOL drops begin automatically after stabilization
                  </Text>
                </VStack>
              </SimpleGrid>
            </VStack>
          </Box>

          {/* Automated Airdrops Section */}
          <Box w="full" bg="blue.900" p={8} rounded="lg" boxShadow="xl">
            <VStack spacing={6}>
              <HStack>
                <Icon as={FaCode} w={8} h={8} color="cyan.400" />
                <Heading size="lg" color="cyan.400">Automated Airdrops</Heading>
              </HStack>
              <Text fontSize="lg" textAlign="center" maxW="800px">
                Our airdrops are fully automated through ICP smart contracts, leveraging QuickNode endpoints to securely access the Solana blockchain.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaServer} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">ICP Smart Contracts</Heading>
                  <Text textAlign="center" color="gray.300">
                    Automated distribution logic runs on Internet Computer
                  </Text>
                </VStack>
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaNetworkWired} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">QuickNode Integration</Heading>
                  <Text textAlign="center" color="gray.300">
                    Reliable Solana access through enterprise-grade endpoints
                  </Text>
                </VStack>
                <VStack p={6} bg="blue.800" rounded="lg" boxShadow="md">
                  <Icon as={FaLock} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">Secure Execution</Heading>
                  <Text textAlign="center" color="gray.300">
                    End-to-end encrypted transactions with multi-signature security
                  </Text>
                </VStack>
              </SimpleGrid>
              <Text fontSize="md" textAlign="center" maxW="800px" color="gray.300">
                <strong>Technical Implementation:</strong> Our ICP canisters communicate with Solana through QuickNode's high-performance RPC endpoints, ensuring reliable and secure cross-chain operations for automated airdrops.
              </Text>
            </VStack>
          </Box>

          {/* Technology Stack Section */}
          <Box w="full">
            <Heading size="xl" textAlign="center" mb={10} color="cyan.400">
              Our Technology Stack
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4}>
                <Heading size="md" color="cyan.400">Frontend</Heading>
                <Text color="gray.300">React.js</Text>
                <Text color="gray.300">Chakra UI</Text>
                <Text color="gray.300">TypeScript</Text>
              </VStack>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4}>
                <Heading size="md" color="cyan.400">Backend</Heading>
                <Text color="gray.300">Internet Computer</Text>
                <Text color="gray.300">Motoko</Text>
                <Text color="gray.300">Candid</Text>
              </VStack>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4}>
                <Heading size="md" color="cyan.400">Blockchain</Heading>
                <Text color="gray.300">Solana</Text>
                <Text color="gray.300">Moonit</Text>
                <Text color="gray.300">Web3.js</Text>
              </VStack>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4}>
                <Heading size="md" color="cyan.400">Infrastructure</Heading>
                <Text color="gray.300">QuickNode</Text>
                <Text color="gray.300">DFX</Text>
                <Text color="gray.300">Git</Text>
              </VStack>
            </SimpleGrid>
          </Box>

          {/* The Power of Our Blockchains Section */}
          <Box w="full">
            <Heading size="xl" textAlign="center" mb={10} color="cyan.400">
              The Power of Our Blockchains
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4} align="start">
                <HStack>
                  <Icon as={FaServer} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">Internet Computer (ICP)</Heading>
                </HStack>
                <Text color="gray.300">
                  The Internet Computer is the world's fastest and most scalable blockchain, offering:
                </Text>
                <VStack align="start" spacing={2} pl={4}>
                  <Text color="gray.300">• Unlimited scalability with sub-second finality</Text>
                  <Text color="gray.300">• Advanced cryptographic security</Text>
                  <Text color="gray.300">• Reverse-gas model for seamless user experience</Text>
                  <Text color="gray.300">• Internet Identity for secure authentication</Text>
                  <Text color="gray.300">• Smart contract automation for cross-chain operations</Text>
                </VStack>
              </VStack>
              <VStack p={8} bg="blue.900" rounded="lg" boxShadow="xl" spacing={4} align="start">
                <HStack>
                  <Icon as={FaBolt} w={8} h={8} color="cyan.400" />
                  <Heading size="md" color="cyan.400">Solana</Heading>
                </HStack>
                <Text color="gray.300">
                  Solana is a high-performance blockchain that provides:
                </Text>
                <VStack align="start" spacing={2} pl={4}>
                  <Text color="gray.300">• Lightning-fast transactions (65,000+ TPS)</Text>
                  <Text color="gray.300">• Extremely low transaction fees</Text>
                  <Text color="gray.300">• Energy-efficient Proof of Stake consensus</Text>
                  <Text color="gray.300">• Robust ecosystem of DeFi applications</Text>
                  <Text color="gray.300">• QuickNode integration for reliable RPC access</Text>
                </VStack>
              </VStack>
            </SimpleGrid>
          </Box>

          {/* Call to Action */}
          <Box textAlign="center" w="full">
            <VStack spacing={6}>
              <Text fontSize="xl" color="gray.300">
                Join us in receiving automatic SOL drops
              </Text>
              <Button
                as={RouterLink}
                to="/how-it-works"
                size="lg"
                colorScheme="cyan"
                bgGradient="linear(to-r, cyan.400, blue.500)"
                _hover={{
                  bgGradient: "linear(to-r, cyan.500, blue.600)",
                }}
              >
                Get Started
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
      <Footer />
    </Box>
  );
};

export default About; 